import { useQuery } from "@tanstack/react-query";
import { BadgeDollarSign, CalendarClock, CircleOff, ListChecks, Scale } from "lucide-react";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { CreditNote, Invoice, ListResponse, Payment, PaymentSchedule } from "../../api/types";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  ReasonDialog,
  StatusPill,
} from "../../ui/components";

type FinanceForm =
  | { kind: "credit" }
  | { kind: "write_off" }
  | { kind: "schedule" }
  | { kind: "reconcile"; payment: Payment }
  | { kind: "dispute"; payment: Payment };

interface InvoiceFinanceControlsProps {
  invoice: Invoice;
  payments: Payment[];
  canRecordPayments: boolean;
  canManageFinance: boolean;
  refresh: () => Promise<unknown>;
}

function moneyMinor(value: FormDataEntryValue | null): number {
  return Math.round(Number(value ?? 0) * 100);
}

export function InvoiceFinanceControls({
  invoice,
  payments,
  canRecordPayments,
  canManageFinance,
  refresh,
}: InvoiceFinanceControlsProps) {
  const sensitive = useSensitiveAction({
    setupTitle: "Protect invoice changes with two-step verification",
    lockedTitle: "Confirm your identity to change this invoice",
    lockedDescription:
      "Credits, write-offs, payment plans, and disputes change the permanent finance record. Enter a fresh code before continuing.",
  });
  const creditNotes = useQuery({
    queryKey: ["invoices", invoice.id, "credit-notes"],
    queryFn: () => api<ListResponse<CreditNote>>(`/api/v1/invoices/${invoice.id}/credit-notes`),
  });
  const schedules = useQuery({
    queryKey: ["invoices", invoice.id, "payment-schedules"],
    queryFn: () =>
      api<ListResponse<PaymentSchedule>>(`/api/v1/invoices/${invoice.id}/payment-schedules`),
  });
  const [form, setForm] = useState<FinanceForm>();
  const [voidTarget, setVoidTarget] = useState<CreditNote>();
  const [scheduleAction, setScheduleAction] = useState<{
    item: PaymentSchedule;
    status: "paid" | "waived" | "overdue";
  }>();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();

  async function refreshAll() {
    await Promise.all([creditNotes.refetch(), schedules.refetch(), refresh()]);
  }

  async function submitForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form) return;
    const data = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      if (form.kind === "credit" || form.kind === "write_off") {
        const path =
          form.kind === "credit"
            ? `/api/v1/invoices/${invoice.id}/credit-notes`
            : `/api/v1/invoices/${invoice.id}/write-off`;
        await api(path, {
          method: "POST",
          body: jsonBody({
            amountMinor: moneyMinor(data.get("amount")),
            reason: String(data.get("reason") ?? "").trim(),
            version: invoice.version,
          }),
        });
      } else if (form.kind === "schedule") {
        await api(`/api/v1/invoices/${invoice.id}/payment-schedules`, {
          method: "POST",
          body: jsonBody({
            kind: String(data.get("kind")),
            amountMinor: moneyMinor(data.get("amount")),
            dueAt: new Date(String(data.get("dueAt"))).toISOString(),
          }),
        });
      } else if (form.kind === "reconcile") {
        await api(`/api/v1/payments/${form.payment.id}/reconciliation`, {
          method: "PUT",
          body: jsonBody({
            state: String(data.get("state")),
            reason: String(data.get("reason") ?? "").trim(),
            version: form.payment.version,
          }),
        });
      } else {
        const due = String(data.get("responseDueAt") ?? "");
        await api(`/api/v1/payments/${form.payment.id}/disputes`, {
          method: "POST",
          body: jsonBody({
            providerId: String(data.get("providerId") ?? "").trim(),
            amountMinor: moneyMinor(data.get("amount")),
            reason: String(data.get("reason") ?? "").trim(),
            status: String(data.get("status")),
            notes: String(data.get("notes") ?? "").trim(),
            responseDueAt: due ? new Date(due).toISOString() : null,
          }),
        });
      }
      await refreshAll();
      setForm(undefined);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function voidCredit(reason: string) {
    if (!voidTarget) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/credit-notes/${voidTarget.id}/void`, {
        method: "POST",
        body: jsonBody({ reason, version: voidTarget.version, status: "void" }),
      });
      await refreshAll();
      setVoidTarget(undefined);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function transitionSchedule(reason: string) {
    if (!scheduleAction) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/payment-schedules/${scheduleAction.item.id}/status`, {
        method: "PUT",
        body: jsonBody({
          status: scheduleAction.status,
          reason,
          version: scheduleAction.item.version,
        }),
      });
      await refreshAll();
      setScheduleAction(undefined);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  const creditItems = creditNotes.data?.items ?? [];
  const scheduleItems = schedules.data?.items ?? [];
  const canAdjustInvoice =
    canManageFinance && ["issued", "part_paid", "paid", "overdue"].includes(invoice.status);
  const canSchedule =
    ["draft", "issued", "part_paid", "overdue"].includes(invoice.status) &&
    invoice.balanceMinor > 0;
  const currentFormPayment =
    form?.kind === "reconcile" || form?.kind === "dispute" ? form.payment : undefined;

  if (form) {
    return (
      <div className="stack">
        <Card>
          <CardHeader
            title={titleCase(form.kind)}
            description="Complete this accounting operation in the invoice workspace."
            actions={
              <button
                className="button secondary small"
                type="button"
                onClick={() => setForm(undefined)}
              >
                Back to invoice controls
              </button>
            }
          />
          {sensitive.view}
          {error ? <ErrorPanel error={error} /> : null}
          <form className="form-grid" onSubmit={submitForm}>
            {form.kind === "credit" || form.kind === "write_off" ? (
              <>
                <Field label={`Amount (${invoice.currency})`}>
                  <input
                    data-autofocus
                    name="amount"
                    type="number"
                    min={0.01}
                    max={
                      (form.kind === "write_off" ? invoice.balanceMinor : invoice.totalMinor) / 100
                    }
                    step={0.01}
                    required
                  />
                </Field>
                <Field
                  label="Audit reason"
                  hint={
                    form.kind === "credit"
                      ? "A credit note is not a cash refund."
                      : "Explain why the balance is uncollectable."
                  }
                >
                  <textarea
                    name="reason"
                    required
                    minLength={form.kind === "write_off" ? 10 : 5}
                    maxLength={1000}
                  />
                </Field>
              </>
            ) : form.kind === "schedule" ? (
              <>
                <Field label="Installment type">
                  <select name="kind" defaultValue="instalment">
                    <option value="deposit">Deposit</option>
                    <option value="instalment">Installment</option>
                    <option value="balance">Balance</option>
                    <option value="damage_deposit">Damage deposit</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </Field>
                <Field label={`Amount (${invoice.currency})`}>
                  <input
                    name="amount"
                    type="number"
                    min={0.01}
                    max={invoice.balanceMinor / 100}
                    step={0.01}
                    required
                  />
                </Field>
                <Field label="Due at">
                  <input name="dueAt" type="datetime-local" required />
                </Field>
              </>
            ) : form.kind === "reconcile" ? (
              <>
                <Field label="Reconciliation result">
                  <select name="state" defaultValue="matched">
                    <option value="matched">Matched</option>
                    <option value="exception">Exception</option>
                    <option value="ignored">Ignored intentionally</option>
                    <option value="unreconciled">Return to unreconciled</option>
                  </select>
                </Field>
                <Field label="Audit reason">
                  <textarea name="reason" required minLength={5} maxLength={1000} />
                </Field>
              </>
            ) : (
              <>
                <Notice tone="warning" title="Dispute record">
                  This records a processor or manual dispute. It does not issue a refund.
                </Notice>
                <Field label="Provider dispute reference">
                  <input
                    name="providerId"
                    defaultValue={currentFormPayment?.providerId ?? ""}
                    required
                    maxLength={255}
                  />
                </Field>
                <Field label={`Disputed amount (${invoice.currency})`}>
                  <input
                    name="amount"
                    type="number"
                    min={0.01}
                    max={(currentFormPayment?.amountMinor ?? 0) / 100}
                    step={0.01}
                    defaultValue={((currentFormPayment?.amountMinor ?? 0) / 100).toFixed(2)}
                    required
                  />
                </Field>
                <Field label="Initial status">
                  <select name="status" defaultValue="needs_response">
                    <option value="warning">Warning</option>
                    <option value="needs_response">Needs response</option>
                    <option value="under_review">Under review</option>
                  </select>
                </Field>
                <Field label="Response due">
                  <input name="responseDueAt" type="datetime-local" />
                </Field>
                <Field label="Dispute reason">
                  <input name="reason" required minLength={1} maxLength={500} />
                </Field>
                <Field label="Internal notes">
                  <textarea name="notes" maxLength={2000} />
                </Field>
              </>
            )}
            <div className="full">
              <FormActions
                submitting={working}
                disabled={!sensitive.unlocked}
                submitLabel={
                  form.kind === "write_off" || form.kind === "dispute"
                    ? "Confirm finance action"
                    : "Save"
                }
                tone={form.kind === "write_off" || form.kind === "dispute" ? "danger" : "primary"}
                onCancel={() => setForm(undefined)}
              />
            </div>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="stack">
      {sensitive.view}
      {error ? <ErrorPanel error={error} /> : null}

      <Card>
        <CardHeader
          title="Credits and write-offs"
          description="Credit notes preserve the legal invoice and post a separate ledger entry. Write-offs settle uncollectable balances without pretending cash was received."
          actions={
            canAdjustInvoice ? (
              <span className="inline">
                <button
                  className="button secondary small"
                  type="button"
                  disabled={!sensitive.unlocked}
                  onClick={() => {
                    setError(undefined);
                    setForm({ kind: "credit" });
                  }}
                >
                  <BadgeDollarSign size={16} /> Credit note
                </button>
                {invoice.balanceMinor > 0 ? (
                  <button
                    className="button danger small"
                    type="button"
                    disabled={!sensitive.unlocked}
                    onClick={() => {
                      setError(undefined);
                      setForm({ kind: "write_off" });
                    }}
                  >
                    <CircleOff size={16} /> Write off
                  </button>
                ) : null}
              </span>
            ) : undefined
          }
        />
        {creditNotes.error ? (
          <ErrorPanel error={creditNotes.error} />
        ) : creditItems.length ? (
          <DataTable
            caption="Invoice credit notes"
            items={creditItems}
            getKey={(item) => item.id}
            columns={[
              { label: "Credit note", render: (item) => item.number },
              {
                label: "Amount",
                className: "numeric",
                render: (item) => formatMoney(item.totalMinor, item.currency),
              },
              {
                label: "Balance impact",
                className: "numeric",
                render: (item) => formatMoney(item.appliedToBalanceMinor, item.currency),
              },
              { label: "Issued", render: (item) => formatDate(item.issuedAt) },
              { label: "Status", render: (item) => <StatusPill value={item.status} /> },
              {
                label: "Action",
                render: (item) =>
                  canManageFinance && item.status === "issued" ? (
                    <button
                      className="button danger small"
                      type="button"
                      disabled={!sensitive.unlocked}
                      onClick={() => {
                        setError(undefined);
                        setVoidTarget(item);
                      }}
                    >
                      Void
                    </button>
                  ) : (
                    item.voidReason || "Not available"
                  ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No credit notes"
            description="Credits are shown separately from refunds so the accounting treatment stays clear."
          />
        )}
      </Card>

      <Card>
        <CardHeader
          title="Payment schedule"
          description="Break the current balance into deposits, installments, or a final balance."
          actions={
            canSchedule ? (
              <button
                className="button secondary small"
                type="button"
                disabled={!sensitive.unlocked}
                onClick={() => {
                  setError(undefined);
                  setForm({ kind: "schedule" });
                }}
              >
                <CalendarClock size={16} /> Add installment
              </button>
            ) : undefined
          }
        />
        {schedules.error ? (
          <ErrorPanel error={schedules.error} />
        ) : scheduleItems.length ? (
          <DataTable
            caption="Invoice payment schedule"
            items={scheduleItems}
            getKey={(item) => item.id}
            columns={[
              { label: "Type", render: (item) => titleCase(item.kind) },
              {
                label: "Amount",
                className: "numeric",
                render: (item) => formatMoney(item.amountMinor, invoice.currency),
              },
              { label: "Due", render: (item) => formatDate(item.dueAt) },
              { label: "Status", render: (item) => <StatusPill value={item.status} /> },
              {
                label: "Action",
                render: (item) =>
                  ["paid", "waived"].includes(item.status) ? (
                    "Not available"
                  ) : (
                    <span className="inline">
                      <button
                        className="button primary small"
                        type="button"
                        disabled={!sensitive.unlocked}
                        onClick={() => setScheduleAction({ item, status: "paid" })}
                      >
                        Paid
                      </button>
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={!sensitive.unlocked}
                        onClick={() => setScheduleAction({ item, status: "overdue" })}
                      >
                        Overdue
                      </button>
                      <button
                        className="button ghost small"
                        type="button"
                        disabled={!sensitive.unlocked}
                        onClick={() => setScheduleAction({ item, status: "waived" })}
                      >
                        Waive
                      </button>
                    </span>
                  ),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No payment schedule"
            description="The invoice can still be paid in one transaction."
          />
        )}
      </Card>

      {canRecordPayments ? (
        <Card>
          <CardHeader
            title="Reconciliation and disputes"
            description="Settlement matching and processor disputes remain separate from refunds."
            actions={<Scale size={19} aria-hidden="true" />}
          />
          {payments.length ? (
            <DataTable
              caption="Payment control status"
              items={payments}
              getKey={(item) => item.id}
              columns={[
                {
                  label: "Payment",
                  render: (item) => `${titleCase(item.provider)} · ${titleCase(item.method)}`,
                },
                {
                  label: "Amount",
                  className: "numeric",
                  render: (item) => formatMoney(item.amountMinor, item.currency),
                },
                { label: "Received", render: (item) => formatDate(item.receivedAt) },
                {
                  label: "Reconciliation",
                  render: (item) => <StatusPill value={item.reconciliationState} />,
                },
                {
                  label: "Action",
                  render: (item) => (
                    <span className="inline">
                      <button
                        className="button secondary small"
                        type="button"
                        disabled={!sensitive.unlocked}
                        onClick={() => {
                          setError(undefined);
                          setForm({ kind: "reconcile", payment: item });
                        }}
                      >
                        <ListChecks size={15} /> Reconcile
                      </button>
                      {canManageFinance &&
                      ["succeeded", "part_refunded", "refunded"].includes(item.status) ? (
                        <button
                          className="button danger small"
                          type="button"
                          disabled={!sensitive.unlocked}
                          onClick={() => {
                            setError(undefined);
                            setForm({ kind: "dispute", payment: item });
                          }}
                        >
                          Dispute
                        </button>
                      ) : null}
                    </span>
                  ),
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No payment controls"
              description="Payment reconciliation becomes available after a payment is recorded."
            />
          )}
        </Card>
      ) : null}

      <ReasonDialog
        open={Boolean(voidTarget)}
        onClose={() => setVoidTarget(undefined)}
        onSubmit={voidCredit}
        title="Void credit note"
        description="The original credit remains visible and an equal reversing ledger entry is posted. Any balance impact is restored."
        submitLabel="Void credit note"
        tone="danger"
        minLength={10}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
      <ReasonDialog
        open={Boolean(scheduleAction)}
        onClose={() => setScheduleAction(undefined)}
        onSubmit={transitionSchedule}
        title={
          scheduleAction
            ? `Mark installment ${titleCase(scheduleAction.status)}`
            : "Update installment"
        }
        description="Record why the expected payment state is changing."
        submitLabel="Update schedule"
        tone={scheduleAction?.status === "waived" ? "danger" : "primary"}
        minLength={5}
        submitting={working}
        disabled={!sensitive.unlocked}
      />
    </div>
  );
}
